import moment from "moment";

// Shared helpers for turning a print into a bucket-ready PDF File: the object-name
// builder and the DOM-to-paginated-PDF rasteriser. The document itself is composed
// elsewhere (see formStructuredPrint.js).

const FILENAME_SAFE = /[^a-zA-Z0-9]+/g;

// Bucket object names ride in a URL path, so anything outside [a-zA-Z0-9] is
// collapsed to a single underscore. Empty/blank parts fall back so the filename
// never ends up with a hole in it (external__1234.pdf).
const sanitizeNamePart = (value, fallback) => {
    const cleaned = String(value ?? "").replace(FILENAME_SAFE, "_").replace(/^_+|_+$/g, "");
    return cleaned || fallback;
};

// external_${stepname}_${username}_${currentTime}.pdf
export const buildFormPrintFileName = ({ stepName, userName, currentTime = moment() }) => {
    const step = sanitizeNamePart(stepName, "step");
    const user = sanitizeNamePart(userName, "user");
    const time = moment(currentTime).format("YYYYMMDDHHmmss");
    return `external_${step}_${user}_${time}.pdf`;
};

// Page furniture for the printed record, in points. The captured document carries
// its own side padding, so only the top and bottom bands are added here - without
// them the capture runs edge to edge and the first line of every page sits against
// the paper edge.
const PAGE_MARGIN_TOP = 28;
const PAGE_MARGIN_BOTTOM = 28;

// A strip has to be worth a page: snapping to a break point that sits in the first
// fifth of the page would leave most of it blank, so a cut is taken there instead.
const MIN_PAGE_FILL = 0.2;

// The last break point that still fits on the page, so a page ends between two
// rows rather than through the middle of one.
const breakWithin = (breakOffsets, start, limit) => {
    let best = 0;
    for (const offset of breakOffsets) {
        if (offset > start && offset <= limit && offset > best) best = offset;
    }
    return best;
};

// Slices a rendered canvas down an A4 portrait PDF, a page of content at a time.
// Each strip is copied onto its own canvas rather than drawn as the full image at
// a negative offset: the page then holds only that strip, leaving the top and
// bottom margins blank instead of filled with the neighbouring rows.
//
// `breakOffsets` are row boundaries in canvas pixels; where one is available the
// page ends on it.
const sliceCanvasToPdfFile = (canvas, fileName, jsPDF, breakOffsets = []) => {
    const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const contentHeight = pageHeight - PAGE_MARGIN_TOP - PAGE_MARGIN_BOTTOM;

    // Canvas pixels to points: the capture is scaled to the full page width.
    const ratio = pageWidth / canvas.width;
    const pageSlice = contentHeight / ratio;

    let offset = 0;
    let page = 0;

    while (offset < canvas.height) {
        let sliceHeight = Math.min(pageSlice, canvas.height - offset);

        // Only pages that have something after them need a clean break; the last
        // strip ends with the document.
        if (offset + sliceHeight < canvas.height) {
            const candidate = breakWithin(breakOffsets, offset + pageSlice * MIN_PAGE_FILL, offset + sliceHeight);
            if (candidate) sliceHeight = candidate - offset;
        }

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.max(1, Math.ceil(sliceHeight));
        const context = pageCanvas.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        context.drawImage(canvas, 0, offset, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

        if (page > 0) pdf.addPage();
        pdf.addImage(pageCanvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, PAGE_MARGIN_TOP, pageWidth, sliceHeight * ratio);

        offset += sliceHeight;
        page += 1;
    }

    const blob = pdf.output("blob");
    return new File([blob], fileName, { type: "application/pdf" });
};

// Row boundaries, read off the mounted element and expressed in canvas pixels, so
// pagination can end a page between two rows. Measured before the capture, since
// the canvas itself carries no structure.
const collectBreakOffsets = (element, canvas) => {
    const blocks = element.querySelectorAll("[data-print-break]");
    if (!blocks.length) return [];

    const top = element.getBoundingClientRect().top;
    const ratio = canvas.height / element.offsetHeight;

    return Array.from(blocks, (block) => (block.getBoundingClientRect().bottom - top) * ratio);
};

// Rasterises any DOM element to a paginated PDF File. html2canvas and jsPDF are
// imported lazily so they stay out of the initial bundle.
export const rasterizeElementToPdfFile = async (element, fileName, captureOptions = {}) => {
    if (!element) {
        throw new Error("Nothing to print: element is not mounted");
    }

    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
    ]);

    const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 15000,
        ...captureOptions,
    });

    if (!canvas.width || !canvas.height) {
        throw new Error("Nothing to print: captured element was empty");
    }

    return sliceCanvasToPdfFile(canvas, fileName, jsPDF, collectBreakOffsets(element, canvas));
};
